import { useCallback, useEffect, useRef, useState } from '@wordpress/element';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import AlignmentTune from './EditorTunes/AlinhamentTune';
import Paragraph from '@editorjs/paragraph';
import Base64ImageTool from './EditorTunes/Base64Image';
import IAGeneratorTune from './EditorTunes/IAGeneratorTune';
import { useIntegracao } from './Context/IntegracaoContext';
import ToneTune from './EditorTunes/ToneTune';
import AlterParagraphTune from './EditorTunes/AlterParagraphTune';

export const EditorComponent = ({ paragrafoGeradoStreaming }) => {
  const ejInstance = useRef();
  const imagemCarregada = useRef(false);
  const { setEditorData, salvarRascunho } = useIntegracao();
  const [blocosPendentes, setBlocosPendentes] = useState([]);
  const [proximoIndice, setProximoIndice] = useState(0);
  const [imagemPendente, setImagemPendente] = useState(null);
  const [primeiroParProcessado, setPrimeiroParProcessado] = useState(false);

  const adicionarImagem = useCallback((base64, legenda = 'Clique aqui para editar a legenda', indice = null) => {
    if (base64 !== '' && ejInstance.current) {
      const indiceParaInserir = indice !== null ? indice : proximoIndice;
      ejInstance.current.blocks.insert(
        'image',
        {
          url: `data:image/png;base64,${base64}`,
          caption: legenda,
        },
        {},
        indiceParaInserir
      );
      if (indice === null) {
        setProximoIndice(prev => prev + 1);
      }
    }
  }, [proximoIndice]);

  useEffect(() => {
    if (imagemPendente && primeiroParProcessado && ejInstance.current) {
      setTimeout(() => {
        if (ejInstance.current) {
          ejInstance.current.blocks.insert(
            'image',
            {
              url: `data:image/png;base64,${imagemPendente.paragrafo}`,
              caption: 'Legenda da Imagem IA',
            },
            {},
            2
          );

          setProximoIndice(prev => Math.max(prev, 3));

          setImagemPendente(null);
        }
      }, 200);
    }
  }, [imagemPendente, primeiroParProcessado]);

  const editarConteudoCallback = useCallback(() => {
    const rascunhoString = localStorage.getItem('artigo_em_edicao');

    if (ejInstance.current && rascunhoString) {
      const rascunhoJson = JSON.parse(rascunhoString);
      if (rascunhoJson) {
        const conteudo = JSON.parse(rascunhoJson?.conteudo);
        ejInstance.current.blocks.insertMany(conteudo?.blocks);

        setProximoIndice(conteudo?.blocks?.length || 0);
      }
    }
  }, [adicionarImagem]);

  useEffect(() => {
    if (paragrafoGeradoStreaming) {
      if (paragrafoGeradoStreaming.titulo === 'imagem') {
        setImagemPendente(paragrafoGeradoStreaming);
      } else {
        setBlocosPendentes(prev => {
          const atualizados = [...prev, paragrafoGeradoStreaming];
          return atualizados.sort((a, b) => a.index - b.index);
        });
      }
    }
  }, [paragrafoGeradoStreaming]);

  useEffect(() => {
    if (ejInstance.current && blocosPendentes.length > 0) {
      blocosPendentes.forEach(bloco => {
        const indiceHeader = proximoIndice;
        ejInstance.current.blocks.insert(
          'header',
          {
            text: bloco.titulo,
            level: bloco.tagHtml == "titulo" ? 1 : bloco.tagHtml == "h1" ? 2 : bloco.tagHtml == "h2" ? 2 : 3,
          },
          {},
          indiceHeader
        );
        const indiceParagrafo = indiceHeader + 1;
        ejInstance.current.blocks.insert(
          'paragraph',
          {
            text: bloco.paragrafo,
            level: 1,
          },
          {},
          indiceParagrafo
        );
        setProximoIndice(prev => prev + 2);
        if (!primeiroParProcessado) {
          setPrimeiroParProcessado(true);
        }
      });

      setBlocosPendentes([]);
    }
  }, [blocosPendentes, proximoIndice, primeiroParProcessado]);

  useEffect(() => {
    if (!ejInstance.current) {
      const editor = new EditorJS({
        holder: 'editorjs',
        data: '',
        autofocus: true,
        onReady: async () => {
          ejInstance.current = editor;
          const savedData = await editor.save();
          setEditorData(savedData);
        },
        onChange: async () => {
          const content = await editor.saver.save();
          setEditorData(content);

          const blocoImagem = content.blocks.some(block => block.type === 'image');
          if (blocoImagem && !imagemCarregada.current) {
            imagemCarregada.current = true;
          }
        },
        tools: {
          header: Header,
          list: List,
          image: {
            class: Base64ImageTool,
            toolbox: { title: 'Subir Imagem' },
          },
          iaImage: {
            class: IAGeneratorTune,
            toolbox: { title: 'Gerar Imagem IA' },
          },
          paragraph: {
            class: Paragraph,
            tunes: ['alignment', 'tone', 'alterParagraph'],
          },
          alterParagraph: AlterParagraphTune,
          alignment: AlignmentTune,
          tone: ToneTune,
        },
        i18n: {
          messages: {
            /**
             * Tradução da interface do Editor.js
             */
            ui: {
              blockTunes: {
                toggler: {
                  'Click to tune': 'Clique para ajustar',
                  'or drag to move': 'ou arraste para mover',
                },
              },
              inlineToolbar: {
                converter: {
                  'Convert to': 'Converter para',
                },
              },
              toolbar: {
                toolbox: {
                  Add: 'Adicionar',
                },
              },
            },

            /**
             * Tradução dos nomes das ferramentas (blocos e inline tools)
             */
            toolNames: {
              Text: 'Parágrafo',
              Heading: 'Cabeçalho',
              'Unordered list': 'Lista não ordenada',
              'Ordered list': 'Lista ordenada',
              List: 'Lista',
              Warning: 'Aviso',
              Checklist: 'Checklist',
              Quote: 'Citação',
              Code: 'Código',
              Delimiter: 'Separador',
              'Raw HTML': 'HTML Bruto',
              Table: 'Tabela',
              Link: 'Link',
              Marker: 'Marcador',
              Bold: 'Negrito',
              Italic: 'Itálico',
              'Convert to': 'Converter para',
              InlineCode: 'Código Inline',
              Image: 'Imagem',
            },

            /**
             * Traduções específicas para os plugins do Editor.js
             */
            tools: {
              warning: {
                Title: 'Título',
                Message: 'Mensagem',
              },
              link: {
                'Add a link': 'Adicionar um link',
              },
              stub: {
                'The block can not be displayed correctly.':
                  'O bloco não pôde ser exibido corretamente.',
              },
              image: {
                'Select an Image': 'Selecionar uma imagem',
                'With border': 'Com borda',
                'Stretch image': 'Esticar imagem',
                'With background': 'Com fundo',
                'Upload a file': 'Enviar um arquivo',
                'Paste an image or image URL': 'Cole uma imagem ou URL de imagem',
              },
              table: {
                'Insert column to left': 'Inserir coluna à esquerda',
                'Insert column to right': 'Inserir coluna à direita',
                'Delete column': 'Excluir coluna',
                'Insert row above': 'Inserir linha acima',
                'Insert row below': 'Inserir linha abaixo',
                'Delete row': 'Excluir linha',
              },
              quote: {
                'Enter a quote': 'Digite uma citação',
                'Enter a quote author': 'Digite o autor da citação',
              },
              list: {
                'Unordered list': 'Lista não ordenada',
                'Ordered list': 'Lista ordenada',
              },
            },

            /**
             * Traduções para os Block Tunes (menu lateral dos blocos)
             */
            blockTunes: {
              delete: {
                Delete: 'Excluir',
              },
              moveUp: {
                'Move up': 'Mover para cima',
              },
              moveDown: {
                'Move down': 'Mover para baixo',
              },
              'Convert to': 'Converter para',
            },
          },
        },
      });
    }

    return () => {
      ejInstance.current?.destroy();
      ejInstance.current = null;
    };
  }, []);

  useEffect(() => {
    editarConteudoCallback();
  }, [editarConteudoCallback]);

  useEffect(() => {
    const url = window.location.href;
    if (ejInstance.current && imagemCarregada.current && url.includes('criar-artigo=true')) {
      salvarRascunho();
    }
  }, [ejInstance.current, imagemCarregada.current]);

  return (
    <>
      <div id="editorjs"></div>
    </>
  );
};